import type { PoiCategory, PoiData, SEOPoiCategory } from '$lib/components/pois/types';
import type {
	FAQData,
	FooterData,
	FooterLinks,
	LinkSegments,
	PlainFooterData,
	SEOData,
} from '$lib/components/seo/types';
import type { Price } from '$lib/components/types';
import type { LanguageCode } from '$lib/i18n/language';
import type { FaqMapPageData } from '$lib/server/metas';
import type { SupportedLocale } from '@viamichelin/business';
import type { PoisStoreContent, StaticMapStoreContent } from '../../../types';
import type { PageLoadEvent } from './$types';
import { isAccommodationMichelinPoi, isAccommodationPoi } from '$lib/components/pois/utils';
import {
	getLinkSegmentsFromHierarchies,
	getLinkSegmentsFromHierarchy,
	getLinkSegmentsFromNearbyCities,
	getSegmentsLabel,
} from '$lib/components/seo/linkSegments';
import { i18nPath, segments2routeSegments } from '$lib/i18n/routing';
import { fillAddressData, fillHomeData, fillHotelsData } from '$lib/seo/faq/maps';
import { getRankedCitiesLinks } from '$lib/seo/footerRankedCities';
import {
	buildPrevNextLinks,
	getBreadcrumbs,
	getPaginationData,
	isSEOPoiCategory,
	pageIsCanonicalUrl,
	pageIsSEOIndexable,
} from '$lib/seo/utils';
import {
	deleteSearchParamFromUrlPath,
	type SearchParams,
	type SelectedAddress,
	setSearchParamOnUrlPath,
} from '$lib/services/urls/searchParams';
import { isVanitySearchParams } from '$lib/services/urls/vanity';
import { filterUndefined } from '$lib/utils/predicates';
import { getImagePreloadData } from './getImagePreloadData';

function formatPageMeta(meta: string, page: number = 1, addressLabel: string = '') {
	return meta.replaceAll('{page}', page.toString()).replaceAll('{addressLabel}', addressLabel);
}

const multiSEOPoiCategories = ['RESTAURANT', 'ACCOMMODATION', 'TOURISM'] satisfies SEOPoiCategory[];

type MultiSEOPoiCategory = (typeof multiSEOPoiCategories)[number];

function isMultiSEOPoiCategory(category: PoiCategory): category is MultiSEOPoiCategory {
	return multiSEOPoiCategories.includes(category as MultiSEOPoiCategory);
}

/**
 * Get the canonical category path for the current page
 *
 * For a vanity url, the category is located directly in the url via a sveltekit param.
 * For a technical url, the category depends on the user selection.
 * - If the user selected a single category, we use that category
 * - If the user selected multiple categories, we check if all the categories are the default ones
 */
function getPageCanonicalCategoryPath(
	event: PageLoadEvent,
	searchParams: SearchParams,
): string | undefined {
	const categoryParam = event.params.category;
	const categoriesSearchParam = searchParams.poiCategories;

	// If the categories are not the default ones, or if the single present category is not a seo category, we don't want a canonical link
	const isVanityCategoryPage = !!categoryParam;
	const isMonoCategoryPage
		= categoriesSearchParam?.length === 1 && isSEOPoiCategory(categoriesSearchParam[0]);
	const isMultiCategoriesPage
		= categoriesSearchParam?.length === multiSEOPoiCategories.length
		&& categoriesSearchParam.every(isMultiSEOPoiCategory);

	if (!isVanityCategoryPage && !isMonoCategoryPage && !isMultiCategoriesPage) {
		return undefined;
	}

	// The category can either be in the url or in the search params (in the case of a address search)
	return isVanityCategoryPage
		? `/${categoryParam}`
		: isMonoCategoryPage
			? `/${categoriesSearchParam[0]}`
			: '';
}

interface GetCanonicalLinkArgs {
	event: PageLoadEvent;
	segments?: LinkSegments;
	selectedAddress?: SelectedAddress;
	searchParams: SearchParams;
	lang: LanguageCode;
}

/**
 * Get the canonical link for the current page
 *
 * The canonical link is useful in the case where we are in a technical url related to a vanity
 * url. Eg if we search for paris in the searchbar, then the result would be the same as if we
 * were on the /map/paris page. In that case, we want to have a canonical link to the /map/paris
 *
 * The canonical link is also useful when the url in the search bar does not exactly match the url
 * of the page. Eg if there is a spelling mistake in the url, we want to have a canonical link to
 * the correct url.
 */
function getCanonicalLink({
	event,
	segments,
	searchParams,
}: GetCanonicalLinkArgs) {
	const categoryPath = getPageCanonicalCategoryPath(event, searchParams);

	// Build the right vanity path based on the data
	const pathWithoutAddress = `/map${categoryPath || ''}`;

	let path = segments
		? `${pathWithoutAddress}/${segments.map(link => link.value).join('/')}`
		: pathWithoutAddress;

	// handle page search param on path
	if (searchParams.page > 1) {
		path = setSearchParamOnUrlPath(path, 'page', searchParams.page.toString());
	} else {
		path = deleteSearchParamFromUrlPath(path, 'page');
	}

	return path;
}

function checkIncludeRelatedCanonical({
	event,
	segments,
	selectedAddress,
	searchParams,
	lang,
}: GetCanonicalLinkArgs) {
	const categoryPath = getPageCanonicalCategoryPath(event, searchParams);

	if (categoryPath === undefined) {
		return false;
	}

	// Build the right vanity path based on the data
	const pathWithoutAddress = `/map${categoryPath || ''}`;

	let path = segments
		? `${pathWithoutAddress}/${segments.map(link => link.value).join('/')}`
		: pathWithoutAddress;

	// handle page search param on path
	if (searchParams.page > 1) {
		path = setSearchParamOnUrlPath(path, 'page', searchParams.page.toString());
	} else {
		path = deleteSearchParamFromUrlPath(path, 'page');
	}

	// In the case of a vanity url
	if (isVanitySearchParams(event.url)) {
		let currentUrlPath = event.url.pathname;
		if (searchParams.page > 1) {
			currentUrlPath = setSearchParamOnUrlPath(
				currentUrlPath,
				'page',
				searchParams.page.toString(),
			);
		}

		return i18nPath(path, lang) !== currentUrlPath;
	}

	// In the case of a search url
	if (selectedAddress && selectedAddress.entityType) {
		return true;
	}

	// In the case of a mono category page with no address
	if (categoryPath) {
		return true;
	}

	return false;
}

interface GetFooterDataArgs {
	staticFooterData?: PlainFooterData;
	currentAddress?: LinkSegments;
	addressLinks?: LinkSegments[];
	rankedCitiesData?: FooterLinks;

}

function getFooterData({
	currentAddress,
	addressLinks,
	staticFooterData,
	rankedCitiesData,
}: GetFooterDataArgs): FooterData {
	if (!currentAddress && staticFooterData) {
		return staticFooterData;
	}

	return {
		type: 'structured',
		addressLinks,
		currentAddress,
		rankedCitiesData,
	};
}

function shouldFilterFaqEntry(
	id: string,
	poisStore: PoisStoreContent,
	minPricePoi?: Price,
): boolean {
	return (
		(id === 'options' && poisStore.metaInfo?.hotelCount === 0)
		|| (id === 'cost' && !minPricePoi?.amount)
		|| (id === 'popular' && poisStore.poisList.length === 0)
		|| (id === 'spa' && !poisStore.metaInfo?.spaCount)
		|| (id === 'pool' && !poisStore.metaInfo?.poolCount)
		|| (id === 'family' && !poisStore.metaInfo?.familyCount)
		|| (id === 'disability' && !poisStore.metaInfo?.disabilityCount)
		|| (id === 'parking' && !poisStore.metaInfo?.parkingCount)
		|| (id === 'types' && !poisStore.metaInfo?.subTypes?.length)
	);
}

interface GetHotelsDataFaqArgs {
	entry: FAQData;
	poisStore: PoisStoreContent;
	linkSegments?: LinkSegments;
	lang: LanguageCode;
}

function getHotelsDataFaq({ entry, poisStore, linkSegments, lang }: GetHotelsDataFaqArgs) {
	const { id, question, response } = entry;
	// Get the minimum price of hotels for only one page.
	const minPricePoi = poisStore.poisList
		.map((poi) => {
			if (isAccommodationPoi(poi) || isAccommodationMichelinPoi(poi)) {
				return poi.startingPrice ?? undefined;
			}
			return undefined;
		})
		.filter(filterUndefined)
		.sort((a, b) => a.amount - b.amount)[0];

	if (shouldFilterFaqEntry(id, poisStore, minPricePoi)) {
		return undefined;
	}

	// Get the 3 most popular hotels
	const popularHotels = poisStore.poisList
		.map((poi) => {
			if ((isAccommodationPoi(poi) || isAccommodationMichelinPoi(poi)) && poi.review?.score) {
				return poi;
			}
			return undefined;
		})
		.filter(filterUndefined)
		.sort((a, b) => (b.review?.score as number) - (a.review?.score as number))
		.slice(0, 3);

	return fillHotelsData({
		question,
		answer: response,
		lang,
		addressSegments: linkSegments ?? [],
		currency: minPricePoi?.currency ?? 'EUR',
		priceMin: minPricePoi?.amount ?? 0,
		spaCount: poisStore.metaInfo?.spaCount ?? 0,
		poolCount: poisStore.metaInfo?.poolCount ?? 0,
		familyCount: poisStore.metaInfo?.familyCount ?? 0,
		handicapCount: poisStore.metaInfo?.disabilityCount ?? 0,
		totalNumberAccomodation: poisStore.metaInfo?.hotelCount ?? 0,
		popularHotels,
		accommodationDetails: poisStore.metaInfo?.subTypes ?? [],
		parkingCount: poisStore.metaInfo?.parkingCount ?? 0,
	});
}

interface GetFaqEntriesArgs {
	lang: LanguageCode;
	faqData: FaqMapPageData;
	poisStore: PoisStoreContent;
	linkSegments?: LinkSegments;
}

function getFaqEntries({ lang, faqData, poisStore, linkSegments }: GetFaqEntriesArgs) {
	return faqData.questions
		.map((entry) => {
			const { question, response } = entry;
			switch (faqData.page) {
				case 'home':
					return fillHomeData({ lang, answer: response, question });
				case 'hotels':
					return getHotelsDataFaq({
						entry,
						poisStore,
						linkSegments,
						lang,
					});
				case 'result':
					return fillAddressData({
						question,
						lang,
						answer: response,
						addressSegments: linkSegments ?? [],
					});
				default:
					return undefined;
			}
		})
		.filter(filterUndefined);
}

interface GetPageSeoDataArgs {
	lang: LanguageCode;
	locale: SupportedLocale;
	poisStore: PoisStoreContent;
	staticMapStore?: StaticMapStoreContent;
	searchParams: SearchParams;
	preconnectOrigins?: string[];
	preloadImages?: string[];
	poiList?: PoiData[];
}

export function getPageSeoData(
	event: PageLoadEvent,
	{ poisStore, lang, locale, searchParams, staticMapStore, poiList }: GetPageSeoDataArgs,
) {
	const categoryFromPathParams = event.params.category;

	const localizedLinkSegments = poisStore.locationLocalizedHierarchies
		? getLinkSegmentsFromHierarchies(poisStore.locationLocalizedHierarchies)
		: undefined;

	const addressLinks = poisStore.nearbyCitiesHierarchies
		? getLinkSegmentsFromNearbyCities(poisStore.nearbyCitiesHierarchies)
		: undefined;

	const currentCityAddress = poisStore.cityHierarchy
		? getLinkSegmentsFromHierarchy(poisStore.cityHierarchy)
		: undefined;

	const selectedAddress = searchParams.selectedAddress;
	const searchParamsCategory = searchParams.poiCategories;

	const {
		faqPageData,
		title: rawTitle,
		description: rawDescription,
		staticFooterData,
		rankedCitiesData,
	} = event.data;

	const pageParam = event.url.searchParams.get('page');
	const page = pageParam ? Number.parseInt(pageParam) : 1;
	const addressLabel = localizedLinkSegments?.[locale]
		? getSegmentsLabel(localizedLinkSegments?.[locale])
		: undefined;

	const title = formatPageMeta(rawTitle, page, addressLabel);
	const description = formatPageMeta(rawDescription, page, addressLabel);

	const segments
		= event.url.pathname === '/'
			? segments2routeSegments(
					['map', searchParamsCategory?.length === 1 ? searchParamsCategory[0] : ''],
					lang,
					true,
				)
			: segments2routeSegments(event.url.pathname.split('/').slice(1), lang);

	const pathWithoutAddress = `/map${categoryFromPathParams ? `/${categoryFromPathParams}` : ''}`;
	const canonicalLink = getCanonicalLink({
		event,
		segments: localizedLinkSegments?.[locale],
		selectedAddress,
		searchParams,
		lang,
	});
	const mustIncludeRelatedCanonical = checkIncludeRelatedCanonical({
		event,
		segments: localizedLinkSegments?.[locale],
		selectedAddress,
		searchParams,
		lang,
	});

	const showAlternates = isVanitySearchParams(event.url);
	const isIndexAllowed = pageIsSEOIndexable(event.url, event.params, mustIncludeRelatedCanonical);
	const pagination = getPaginationData(page, poisStore.pageCount, isIndexAllowed);

	return {
		canonicalLink,
		header: {
			allowIndex: isIndexAllowed,
			pathWithoutAddress,
			localizedLinkSegments,
			og: {
				title,
				description,
			},
			canonicalLink: mustIncludeRelatedCanonical ? canonicalLink : undefined,
			showAlternates,
			...(poisStore.poiCategories.length > 1 ? {} : buildPrevNextLinks(event.url, page, poisStore.pageCount)),
			...getImagePreloadData(poiList ?? []),
		},
		footer: {
			faq: faqPageData
				? getFaqEntries({
						lang,
						faqData: faqPageData,
						poisStore,
						linkSegments: localizedLinkSegments?.[locale],
					})
				: undefined,
			title,
			description,
			crumbs: getBreadcrumbs({
				params: event.params,
				searchParams,
				segments,
				locale,
				localizedLinkSegments,
			}),
			data: getFooterData({
				currentAddress: currentCityAddress,
				addressLinks,
				staticFooterData,
				rankedCitiesData: getRankedCitiesLinks(lang, currentCityAddress, rankedCitiesData),
			}),
			pagination:
				pagination && pagination?.pageCount > 1 && pageIsCanonicalUrl(event.url, event.params)
					? pagination
					: undefined,
			staticMap: staticMapStore
				? {
						url: staticMapStore.url,
						height: staticMapStore.height,
						width: staticMapStore.width,
					}
				: undefined,
		},
		arrivalHierarchyPath: localizedLinkSegments ? localizedLinkSegments[locale].map(({ value }) => value).join('/') : undefined,
	} satisfies SEOData;
}
